<template>
    <div class="dep-page">
        <!-- Header -->
        <div class="dep-header">
            <div class="dep-header-left">
                <div class="dep-icon-wrap"><i class="fas fa-chart-line"></i></div>
                <h2 class="dep-title">Depreciation Report</h2>
                <span class="dep-count" v-if="!loading">{{ filteredAssets.length }} assets</span>
            </div>
            <div class="dep-header-right">
                <div class="dep-search-wrap">
                    <i class="fas fa-search dep-search-icon"></i>
                    <input class="dep-search" v-model="search" placeholder="Search asset..." />
                    <button class="dep-search-clear" v-if="search" @click="search = ''"><i class="fas fa-times"></i></button>
                </div>
                <div class="dep-select-wrap">
                    <select class="dep-select" v-model="filterCategory">
                        <option value="">All Categories</option>
                        <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Summary Cards -->
        <div class="dep-summary-row" v-if="!loading && filteredAssets.length">
            <div class="dep-summary-card">
                <span class="dep-summary-label">Total Purchase Value</span>
                <span class="dep-summary-val">₹ {{ fmt(totalPurchaseValue) }}</span>
            </div>
            <div class="dep-summary-card">
                <span class="dep-summary-label">Total Depreciated</span>
                <span class="dep-summary-val dep-val-red">₹ {{ fmt(totalDepreciated) }}</span>
            </div>
            <div class="dep-summary-card">
                <span class="dep-summary-label">Current Book Value</span>
                <span class="dep-summary-val dep-val-green">₹ {{ fmt(totalBookValue) }}</span>
            </div>
            <div class="dep-summary-card">
                <span class="dep-summary-label">Avg. Depreciation Rate</span>
                <span class="dep-summary-val">{{ avgDepRate }}% / yr</span>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="dep-loading"><div class="dep-spinner"></div><span>Loading...</span></div>

        <!-- Empty -->
        <div v-else-if="!filteredAssets.length" class="dep-empty">
            <div class="dep-empty-icon"><i class="fas fa-chart-line"></i></div>
            <h3>{{ search || filterCategory ? 'No assets match your filters' : 'No assets with price data found' }}</h3>
            <p v-if="!search && !filterCategory">Add a price to assets to see depreciation here.</p>
        </div>

        <!-- Table -->
        <div v-else class="dep-table-wrap">
            <table class="dep-table">
                <thead>
                    <tr>
                        <th class="th-idx">#</th>
                        <th>Asset</th>
                        <th>Category</th>
                        <th class="text-right">Purchase Price</th>
                        <th class="text-center">Added On</th>
                        <th class="text-center">Rate / Yr</th>
                        <th class="text-center">Yrs Used</th>
                        <th class="text-right">Depreciated</th>
                        <th class="text-right">Book Value</th>
                        <th class="text-center">Details</th>
                    </tr>
                </thead>
                <tbody v-for="(asset, idx) in filteredAssets" :key="asset.id">
                        <!-- Main row -->
                        <tr class="dep-main-row" :class="{ 'dep-row-open': expanded[asset.id] }">
                            <td class="td-idx">{{ idx + 1 }}</td>
                            <td>
                                <div class="td-asset">
                                    <span class="td-asset-icon" :class="asColor(idx)"><i class="fas fa-box"></i></span>
                                    <div>
                                        <div class="td-asset-name">{{ asset.name }}</div>
                                        <div class="td-asset-serial" v-if="asset.serialNo">{{ asset.serialNo }}</div>
                                    </div>
                                </div>
                            </td>
                            <td class="td-muted">{{ asset.categoryName || '—' }}</td>
                            <td class="text-right td-price">₹ {{ fmt(asset.price) }}</td>
                            <td class="text-center td-muted">{{ formatDate(asset.createdAt) }}</td>
                            <td class="text-center">
                                <span class="dep-rate-badge">{{ asset.depRate }}%</span>
                            </td>
                            <td class="text-center td-muted">{{ asset.yearsUsed }}</td>
                            <td class="text-right td-dep">- ₹ {{ fmt(asset.totalDepreciated) }}</td>
                            <td class="text-right">
                                <div class="td-bookval">
                                    <span class="td-bookval-num">₹ {{ fmt(asset.currentBookValue) }}</span>
                                    <div class="dep-bar-wrap">
                                        <div class="dep-bar" :style="{ width: asset.bookValPct + '%' }"></div>
                                    </div>
                                </div>
                            </td>
                            <td class="text-center">
                                <button class="dep-expand-btn" @click="toggleExpand(asset.id)">
                                    <i :class="expanded[asset.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                    {{ expanded[asset.id] ? 'Hide' : 'Year-wise' }}
                                </button>
                            </td>
                        </tr>

                        <!-- Expanded year-wise breakdown -->
                        <tr v-if="expanded[asset.id]" class="dep-expand-row">
                            <td colspan="10" class="dep-expand-cell">
                                <div class="dep-yearly-wrap">
                                    <div class="dep-yearly-title">
                                        <i class="fas fa-calendar-alt"></i> Year-wise Depreciation — {{ asset.name }}
                                    </div>
                                    <table class="dep-yearly-table">
                                        <thead>
                                            <tr>
                                                <th>Year</th>
                                                <th class="text-right">Opening Value</th>
                                                <th class="text-right">Depreciation ({{ asset.depRate }}%)</th>
                                                <th class="text-right">Closing Book Value</th>
                                                <th class="text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in asset.yearlyRows" :key="row.year" :class="{ 'dep-yr-current': row.isCurrent }">
                                                <td class="td-year">
                                                    <span class="dep-yr-label" :class="{ 'dep-yr-current-label': row.isCurrent }">
                                                        {{ row.year }}
                                                        <span class="dep-current-tag" v-if="row.isCurrent">Current</span>
                                                    </span>
                                                </td>
                                                <td class="text-right">₹ {{ fmt(row.opening) }}</td>
                                                <td class="text-right td-dep">- ₹ {{ fmt(row.depreciation) }}</td>
                                                <td class="text-right td-bookval-num">₹ {{ fmt(row.closing) }}</td>
                                                <td class="text-center">
                                                    <span class="dep-yr-status" :class="row.closing > 0 ? 'dep-yr-active' : 'dep-yr-zero'">
                                                        {{ row.closing > 0 ? 'Active' : 'Fully Depreciated' }}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </td>
                        </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { http } from "../../utils/http"

export default {
    data() {
        return {
            loading: true,
            rawAssets: [],
            categories: [],
            search: "",
            filterCategory: "",
            expanded: {},
        }
    },
    mounted() { this.init() },
    computed: {
        filteredAssets() {
            return this.computedAssets.filter(a => {
                const matchSearch = !this.search ||
                    a.name.toLowerCase().includes(this.search.toLowerCase()) ||
                    (a.serialNo || "").toLowerCase().includes(this.search.toLowerCase()) ||
                    (a.categoryName || "").toLowerCase().includes(this.search.toLowerCase())
                const matchCat = !this.filterCategory || String(a.assetCategoryId) === String(this.filterCategory)
                return matchSearch && matchCat
            })
        },
        computedAssets() {
            const catMap = {}
            for (const c of this.categories) catMap[c.id] = c

            return this.rawAssets
                .filter(a => a.price && Number(a.price) > 0)
                .map(a => {
                    const cat = catMap[a.assetCategoryId] || {}
                    const depRate = Number(cat.depreciationPercentPerYear || 0)
                    const price   = Number(a.price)
                    const startYear = a.createdAt ? new Date(a.createdAt).getFullYear() : new Date().getFullYear()
                    const currentYear = new Date().getFullYear()
                    const yearsUsed = Math.max(currentYear - startYear + 1, 1)

                    // WDV (reducing balance) year-wise calculation
                    const yearlyRows = []
                    let opening = price
                    for (let yr = startYear; yr <= currentYear; yr++) {
                        const depreciation = parseFloat((opening * depRate / 100).toFixed(2))
                        const closing      = Math.max(parseFloat((opening - depreciation).toFixed(2)), 0)
                        yearlyRows.push({
                            year: yr,
                            opening,
                            depreciation,
                            closing,
                            isCurrent: yr === currentYear,
                        })
                        opening = closing
                        if (opening === 0) break
                    }

                    const currentBookValue  = yearlyRows.length ? yearlyRows[yearlyRows.length - 1].closing : price
                    const totalDepreciated  = parseFloat((price - currentBookValue).toFixed(2))
                    const bookValPct        = price > 0 ? Math.round((currentBookValue / price) * 100) : 0

                    return {
                        ...a,
                        categoryName:     cat.name || "—",
                        depRate,
                        yearsUsed,
                        yearlyRows,
                        currentBookValue,
                        totalDepreciated,
                        bookValPct,
                    }
                })
                .sort((a, b) => b.totalDepreciated - a.totalDepreciated)
        },
        totalPurchaseValue() { return this.filteredAssets.reduce((s, a) => s + Number(a.price), 0) },
        totalDepreciated()   { return this.filteredAssets.reduce((s, a) => s + a.totalDepreciated, 0) },
        totalBookValue()     { return this.filteredAssets.reduce((s, a) => s + a.currentBookValue, 0) },
        avgDepRate() {
            if (!this.filteredAssets.length) return 0
            const sum = this.filteredAssets.reduce((s, a) => s + a.depRate, 0)
            return (sum / this.filteredAssets.length).toFixed(1)
        },
    },
    methods: {
        fmt(val) {
            return Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })
        },
        formatDate(d) {
            if (!d) return "—"
            return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        },
        asColor(i) {
            return ["dep-clr-blue","dep-clr-purple","dep-clr-teal","dep-clr-amber","dep-clr-rose"][i % 5]
        },
        toggleExpand(id) {
            this.expanded = { ...this.expanded, [id]: !this.expanded[id] }
        },
        async init() {
            this.loading = true
            try {
                const [assetRes, catRes] = await Promise.all([
                    http.get("/assets?type=ALL"),
                    http.get("/asset-categories"),
                ])
                this.rawAssets  = assetRes.data.result || []
                this.categories = catRes.data.result  || []
            } catch (e) { console.error("depreciation report error", e) }
            this.loading = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.dep-page { animation: depFade .3s ease; }
@keyframes depFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }

/* ── Header ── */
.dep-header {
    display:flex; align-items:center; justify-content:space-between;
    gap:12px; background:white; border:1px solid $gray100; border-radius:8px;
    padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap;
}
.dep-header-left  { display:flex; align-items:center; gap:10px; }
.dep-header-right { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.dep-icon-wrap {
    width:32px; height:32px; border-radius:8px;
    background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%));
    color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px;
}
.dep-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.dep-count {
    min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull;
    font-size:11px; font-weight:700; background:$primaryColor; color:white;
    display:inline-flex; align-items:center; justify-content:center;
}

/* ── Search ── */
.dep-search-wrap {
    display:flex; align-items:center; gap:7px; border:1px solid $gray200;
    border-radius:7px; padding:5px 10px; background:white; transition:border-color .15s;
    &:focus-within { border-color:$primaryColor; box-shadow:0 0 0 3px rgba($primaryColor,.08); }
}
.dep-search-icon { font-size:11px; color:$gray400; flex-shrink:0; }
.dep-search {
    border:none; outline:none; font-size:12.5px; font-family:inherit;
    color:$gray700; background:transparent; width:160px;
    &::placeholder { color:$gray400; }
}
.dep-search-clear { border:none; background:none; cursor:pointer; color:$gray400; padding:0; font-size:10px; &:hover { color:$gray600; } }
.dep-select-wrap select.dep-select {
    border:1px solid $gray200; border-radius:7px; padding:6px 10px;
    font-size:12.5px; font-family:inherit; color:$gray700; background:white;
    outline:none; cursor:pointer; transition:border-color .15s;
    &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba($primaryColor,.08); }
}

/* ── Summary Cards ── */
.dep-summary-row {
    display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:12px;
}
.dep-summary-card {
    background:white; border:1px solid $gray100; border-radius:8px; padding:12px 16px;
    box-shadow:$shadowSm; display:flex; flex-direction:column; gap:4px;
}
.dep-summary-label { font-size:11px; color:$gray400; font-weight:600; text-transform:uppercase; letter-spacing:0.4px; }
.dep-summary-val   { font-size:16px; font-weight:700; color:$gray800; }
.dep-val-red   { color:#dc2626; }
.dep-val-green { color:#16a34a; }

/* ── Loading / Empty ── */
.dep-loading {
    display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px;
    background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm;
    span { font-size:13px; color:$gray400; }
}
.dep-spinner {
    width:24px; height:24px; border:2.5px solid $gray200;
    border-top-color:$primaryColor; border-radius:50%; animation:depSpin .7s linear infinite;
}
@keyframes depSpin { to { transform:rotate(360deg); } }
.dep-empty {
    text-align:center; padding:64px 16px; background:white;
    border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm;
    h3 { font-size:14px; font-weight:700; color:$gray600; margin:0 0 6px; }
    p  { font-size:12px; color:$gray400; margin:0; }
}
.dep-empty-icon {
    width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100;
    display:flex; align-items:center; justify-content:center; margin:0 auto 12px;
    i { font-size:20px; color:$gray300; }
}

/* ── Main Table ── */
.dep-table-wrap { background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; overflow:hidden; }
.dep-table { width:100%; border-collapse:collapse; }
.dep-table thead { background:$gray50; border-bottom:2px solid $gray100; }
.dep-table th {
    padding:10px 14px; font-size:11px; font-weight:600; color:$gray500;
    text-transform:uppercase; letter-spacing:0.3px; white-space:nowrap;
}
.dep-table td {
    padding:10px 14px; font-size:12.5px; color:$gray700; font-weight:500;
    border-bottom:1px solid $gray100; vertical-align:middle;
}
.dep-main-row {
    transition:background $transitionFast;
    &:hover { background:$gray50; }
    &.dep-row-open { background:#fafbff; }
}
.th-idx, .td-idx { width:36px; text-align:center; color:$gray400; font-size:11px; }
.text-right  { text-align:right; }
.text-center { text-align:center; }
.td-muted    { color:$gray500; font-size:12px; }
.td-price    { font-weight:700; color:$gray800; }
.td-dep      { color:#dc2626; font-weight:600; }
.td-bookval-num { font-weight:700; color:#16a34a; }

/* Asset cell */
.td-asset { display:flex; align-items:center; gap:9px; }
.td-asset-icon {
    width:30px; height:30px; border-radius:6px;
    display:inline-flex; align-items:center; justify-content:center;
    font-size:11px; flex-shrink:0;
}
.dep-clr-blue   { background:#eff6ff; color:#2563eb; }
.dep-clr-purple { background:#f5f3ff; color:#7c3aed; }
.dep-clr-teal   { background:#f0fdfa; color:#0d9488; }
.dep-clr-amber  { background:#fffbeb; color:#d97706; }
.dep-clr-rose   { background:#fff1f2; color:#e11d48; }
.td-asset-name   { font-size:13px; font-weight:600; color:$gray800; }
.td-asset-serial { font-size:10.5px; color:$gray400; margin-top:1px; }

/* Rate badge */
.dep-rate-badge {
    display:inline-block; padding:2px 8px; border-radius:$radiusFull;
    background:#fff7ed; color:#c2410c; font-size:11px; font-weight:700;
}

/* Book value bar */
.td-bookval { display:flex; flex-direction:column; align-items:flex-end; gap:4px; }
.dep-bar-wrap { width:80px; height:4px; background:$gray100; border-radius:2px; overflow:hidden; }
.dep-bar { height:100%; background:linear-gradient(90deg, #16a34a, #4ade80); border-radius:2px; transition:width .4s ease; }

/* Expand button */
.dep-expand-btn {
    display:inline-flex; align-items:center; gap:5px; padding:4px 10px;
    border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600;
    cursor:pointer; background:white; color:$primaryColor; font-family:inherit;
    transition:all $transitionFast;
    i { font-size:9px; }
    &:hover { background:$primaryLight; border-color:rgba($primaryColor,.2); }
}

/* ── Expanded Year-wise ── */
.dep-expand-row td { padding:0; border-bottom:2px solid $gray200; }
.dep-expand-cell { padding:0 !important; }
.dep-yearly-wrap {
    background:#f8faff; border-top:1px solid #e0e8ff; padding:14px 20px 16px;
}
.dep-yearly-title {
    font-size:12px; font-weight:700; color:$primaryColor; margin-bottom:10px;
    display:flex; align-items:center; gap:7px;
    i { font-size:11px; }
}
.dep-yearly-table {
    width:100%; border-collapse:collapse; background:white;
    border:1px solid $gray100; border-radius:7px; overflow:hidden;
    th {
        padding:7px 14px; font-size:10.5px; font-weight:600; color:$gray500;
        text-transform:uppercase; letter-spacing:0.3px; background:$gray50;
        border-bottom:1px solid $gray100;
    }
    td {
        padding:7px 14px; font-size:12px; color:$gray700; border-bottom:1px solid $gray100;
        &:last-child { border-bottom:none; }
    }
    tbody tr {
        &:last-child td { border-bottom:none; }
        &:hover { background:$gray50; }
        &.dep-yr-current { background:#f0fdf4; }
    }
}
.td-year { font-weight:600; color:$gray700; }
.dep-yr-label { display:flex; align-items:center; gap:6px; }
.dep-yr-current-label { color:#16a34a; }
.dep-current-tag {
    font-size:9px; font-weight:700; padding:1px 6px; border-radius:$radiusFull;
    background:#dcfce7; color:#16a34a; text-transform:uppercase; letter-spacing:0.3px;
}
.dep-yr-status {
    display:inline-block; font-size:10px; font-weight:700; padding:2px 8px;
    border-radius:$radiusFull;
}
.dep-yr-active { background:#f0fdf4; color:#16a34a; }
.dep-yr-zero   { background:$gray100; color:$gray400; }

/* ── Responsive ── */
@media(max-width:1200px) { .dep-table-wrap { overflow-x:auto; } }
@media(max-width:900px)  { .dep-summary-row { grid-template-columns:repeat(2,1fr); } }
@media(max-width:768px)  {
    .dep-header { flex-direction:column; align-items:stretch; }
    .dep-header-right { flex-direction:column; align-items:flex-start; }
    .dep-summary-row { grid-template-columns:1fr 1fr; }
}
</style>
